/**
 * BrandLogo — SVG inline da marca Amazonas Inovare
 * Variantes: horizontal (logo + texto), vertical, symbol (apenas icone)
 * Cores: white (sobre fundos escuros), teal (sobre fundos claros), dark
 *
 * Referencia: Manual de Identidade Visual Amazonas Inovare (Edicao 1.0)
 * - Minimo horizontal: 150px / 20mm
 * - Minimo vertical: 86px / 11mm
 * - Minimo symbol: 45px / 7mm
 */

interface BrandLogoProps {
  variant?: 'horizontal' | 'vertical' | 'symbol'
  color?: 'white' | 'teal' | 'dark'
  width?: number
  className?: string
}

const COLOR_MAP = {
  white: { primary: '#ffffff', secondary: '#ffffff' },
  teal: { primary: '#025468', secondary: '#098d93' },
  dark: { primary: '#1a2e35', secondary: '#025468' },
}

function LogoSymbol({ primary, secondary, size }: { primary: string; secondary: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Chevron outer (larger) */}
      <path
        d="M32 4L58 32L32 60L6 32L32 4Z"
        stroke={primary}
        strokeWidth="3"
        fill="none"
      />
      {/* Diamond inner */}
      <path
        d="M32 16L46 32L32 48L18 32L32 16Z"
        fill={secondary}
        opacity="0.9"
      />
      {/* Center triangle — 60deg grid element */}
      <path
        d="M32 22L40 36H24L32 22Z"
        fill={primary}
      />
    </svg>
  )
}

export function BrandLogo({ variant = 'horizontal', color = 'teal', width, className }: BrandLogoProps) {
  const { primary, secondary } = COLOR_MAP[color]

  if (variant === 'symbol') {
    const size = width ?? 32
    return (
      <span className={className} style={{ display: 'inline-flex', alignItems: 'center' }}>
        <LogoSymbol primary={primary} secondary={secondary} size={size} />
      </span>
    )
  }

  if (variant === 'vertical') {
    const w = width ?? 86
    const symbolSize = w * 0.6
    return (
      <span className={className} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <LogoSymbol primary={primary} secondary={secondary} size={symbolSize} />
        <svg width={w} height={w * 0.22} viewBox="0 0 160 36" xmlns="http://www.w3.org/2000/svg">
          <text
            x="80" y="14"
            textAnchor="middle"
            fontFamily="Barlow, Arial, sans-serif"
            fontWeight="700"
            fontSize="16"
            fill={primary}
            letterSpacing="0.08em"
          >
            AMAZONAS
          </text>
          <text
            x="80" y="32"
            textAnchor="middle"
            fontFamily="Barlow, Arial, sans-serif"
            fontWeight="400"
            fontSize="12"
            fill={secondary}
            letterSpacing="0.12em"
          >
            INOVARE
          </text>
        </svg>
      </span>
    )
  }

  // horizontal (default)
  const w = width ?? 150
  const symbolSize = Math.min(w * 0.22, 36)
  return (
    <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <LogoSymbol primary={primary} secondary={secondary} size={symbolSize} />
      <svg width={w * 0.72} height={symbolSize} viewBox="0 0 140 36" xmlns="http://www.w3.org/2000/svg">
        <text
          x="0" y="15"
          fontFamily="Barlow, Arial, sans-serif"
          fontWeight="700"
          fontSize="16"
          fill={primary}
          letterSpacing="0.06em"
        >
          AMAZONAS
        </text>
        <text
          x="0" y="32"
          fontFamily="Barlow, Arial, sans-serif"
          fontWeight="400"
          fontSize="12"
          fill={secondary}
          letterSpacing="0.1em"
        >
          INOVARE
        </text>
      </svg>
    </span>
  )
}
